import { useState } from 'react';
import type { Attachment, CaptureBusyState, FeedbackInline, WidgetStep } from '../types.js';
import { shouldSkipIdentity } from '../utils/buildSteps.js';

export interface WizardSnapshot {
  stepIndex: number;
  rating: number | null;
  comment: string;
  selectedType: string | null;
  email: string;
  fullName: string;
  attachments: Attachment[];
  busy: CaptureBusyState;
  submitting: boolean;
  submitted: boolean;
  error: string | null;
}

export interface WizardActions {
  setStepIndex: (index: number) => void;
  setRating: (rating: number) => void;
  setComment: (comment: string) => void;
  setSelectedType: (type: string | null) => void;
  setEmail: (email: string) => void;
  setFullName: (fullName: string) => void;
  addAttachment: (attachment: Attachment) => void;
  removeAttachment: (index: number) => void;
  setBusy: (state: CaptureBusyState) => void;
  setSubmitting: (submitting: boolean) => void;
  setSubmitted: (submitted: boolean) => void;
  setError: (message: string | null) => void;
  advance: (contentSteps: WidgetStep[], known: { email?: string; fullName?: string }) => number | 'submit';
  retreat: () => void;
}

export function useFeedbackWizard(initial: {
  email?: string;
  fullName?: string;
  inline: FeedbackInline | null;
}): [WizardSnapshot, WizardActions] {
  const [stepIndex, setStepIndex] = useState(0);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(initial.inline?.type ?? null);
  const [email, setEmail] = useState(initial.email ?? '');
  const [fullName, setFullName] = useState(initial.fullName ?? '');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState<CaptureBusyState>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addAttachment = (attachment: Attachment): void => {
    setAttachments((prev) => [...prev, attachment]);
  };
  const removeAttachment = (index: number): void => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const advance: WizardActions['advance'] = (contentSteps, known) => {
    let nextIndex = stepIndex + 1;
    while (nextIndex < contentSteps.length) {
      const next = contentSteps[nextIndex];
      if (next && shouldSkipIdentity(next, known.email, known.fullName)) {
        nextIndex += 1;
        continue;
      }
      break;
    }
    if (nextIndex >= contentSteps.length) return 'submit';
    setStepIndex(nextIndex);
    return nextIndex;
  };

  const retreat = (): void => {
    setStepIndex((index) => Math.max(0, index - 1));
  };

  const snapshot: WizardSnapshot = {
    stepIndex,
    rating,
    comment,
    selectedType,
    email,
    fullName,
    attachments,
    busy,
    submitting,
    submitted,
    error,
  };

  const actions: WizardActions = {
    setStepIndex,
    setRating,
    setComment,
    setSelectedType,
    setEmail,
    setFullName,
    addAttachment,
    removeAttachment,
    setBusy,
    setSubmitting,
    setSubmitted,
    setError,
    advance,
    retreat,
  };

  return [snapshot, actions];
}

export function isStepRequirementFilled(step: WidgetStep | undefined, snapshot: WizardSnapshot): boolean {
  if (!step) return true;
  // Rating defaults to required — only an explicit `required: false` opts out.
  if (step.kind === 'rating' && step.required !== false) {
    return snapshot.rating !== null;
  }
  if (!step.required) return true;
  switch (step.kind) {
    case 'type-picker':
      return snapshot.selectedType !== null;
    case 'comment':
      return snapshot.comment.trim().length > 0;
    case 'identity':
      if (step.identityRequired === 'both') return Boolean(snapshot.email && snapshot.fullName);
      if (step.identityRequired === 'email') return Boolean(snapshot.email);
      return true;
    default:
      return true;
  }
}
